import { Drawing } from "./drawing";
import { Shape } from "./shape";
import { Segment, CurveSegment, LineSegment } from "../base/segment";
import { Pt, pt, ptEquals } from "../base/pt";
import { Chain } from "../base/chain";
import { Matrix } from "../base/matrix";
import { Element } from "./svg";
import { loopyThomas } from "./spline";

type StepType = "straight" | "curved";
interface Step {
  kind: StepType;
  dest: Pt;
}

class Path extends Drawing {
  origin: Pt;
  steps: Step[];

  constructor(
    origin: Pt,
    steps: Step[],
    color: string,
    labels: Map<string, Pt>,
    anchorPt?: Pt
  ) {
    super(color, labels, anchorPt);
    this.origin = origin;
    this.steps = steps;
  }

  transform(matrix: Matrix): Path {
    return new Path(
      matrix.transform(this.origin),
      this.transformSteps(matrix),
      this.color,
      this.transformLabels(matrix),
      this.transformAnchor(matrix)
    );
  }

  label(name: string, pt: Pt): Path {
    const newLabels = new Map(this.labels);
    newLabels.set(name, pt);
    return new Path(this.origin, this.steps, this.color, newLabels, this.anchorPt);
  }

  anchorAt(pt: Pt): Path {
    return new Path(this.origin, this.steps, this.color, this.labels, pt)
  }

  stroke(color: string): Path {
    return new Path(this.origin, this.steps, color, this.labels, this.anchorPt)
  }

  private transformSteps(matrix): Step[] {
    return this.steps.map((step) => {
      let newSrc = matrix.transform(step.dest);
      let newStep = { kind: step.kind, dest: newSrc };
      return newStep;
    });
  }

  get start(): Pt {
    return this.pt(0);
  }

  get end(): Pt {
    return this.pt(this.steps.length);
  }

  get elements(): Element[] {
    return [
      {
        chain: buildChain(this.origin, this.steps),
        stroke: this.color,
        fill: "none",
        opacity: 1.0,
      },
    ];
  }

  get drawings() {
    return [this];
  }

  get points(): Pt[] {
    let pts = [this.origin];
    this.steps.forEach((s) => pts.push(s.dest));
    return pts;
  }

  get chains(): Chain[] {
    return [buildChain(this.origin, this.steps)];
  }
}

function curves(pts: Pt[]): Segment[] {
  const xys = pts.map((p) => [p.x, p.y]);
  const controlPoints = loopyThomas(xys).map((a) => {
    let obj = { p1: pt(a[0][0], a[0][1]), p2: pt(a[1][0], a[1][1]) };
    return obj;
  });
  const result = [];
  for (let i = 0; i < pts.length - 1; i++) {
    result.push(
      new CurveSegment(
        pts[i],
        controlPoints[i].p1,
        controlPoints[i].p2,
        pts[i + 1]
      )
    );
  }
  return result;
}

function buildChain(origin: Pt, steps: Step[]): Chain {
  let segments = [];
  let curveBuf = [];
  let src: Pt = origin;
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    if (step.kind == "straight") {
      if (curveBuf.length > 0) {
        curveBuf.push(step.dest);
        segments = segments.concat(curves(curveBuf).slice(0, -1));
        curveBuf = [];
      }
      segments.push(new LineSegment(src, step.dest));
    } else if (step.kind == "curved") {
      if (curveBuf.length == 0) curveBuf.push(src);
      curveBuf.push(step.dest);
    }
    src = step.dest;
  }
  if (curveBuf.length > 0) {
    segments = segments.concat(curves(curveBuf));
  }
  return new Chain(segments);
}

function line(x: number, y = 0): Path {
  return new Path(
    pt(0, 0),
    [{ kind: "straight", dest: pt(x, y) }],
    "black",
    new Map()
  );
}

function join(paths: Path[]) {
  const firstPath = paths[0];
  let newSteps = firstPath.steps;
  const newLabels = new Map(firstPath.labels);
  let start = firstPath.end;
  for (let p of paths.slice(1)) {
    const newP = <Path>p.anchorTo(0).snap(start);
    newSteps = newSteps.concat(newP.steps);
    for (let [key, value] of newP.labels.entries()) {
      newLabels.set(key, value);
    }
    start = newP.end;
  }
  return new Path(firstPath.origin, newSteps, firstPath.color, newLabels);
}

function spline(paths: Path[]) {
  const joined = join(paths);
  const curveSteps: Step[] = joined.steps.map((s) => {
    return { kind: "curved", dest: s.dest };
  });
  return new Path(joined.origin, curveSteps, joined.color, joined.labels);
}

function close(path: Path, color?: string, opacity?: number): Shape {
  var chain: Chain;
  if (ptEquals(path.start, path.end)) {
    chain = buildChain(path.start, path.steps);
  } else {
    let lastKind = path.steps[path.steps.length - 1].kind;
    let step = { kind: lastKind, dest: path.start };
    chain = buildChain(path.start, path.steps.concat(step));
  }
  return new Shape([chain], path.color, path.labels);
}

export { line, join, spline, close, Path };
